import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Calculator,
  Users,
  Clock,
  Moon,
  Sun,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle,
  Download,
  Loader2,
  AlertCircle,
  Euro,
  FileText,
  Edit3,
  X,
  Check
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const VariablesPaie = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Period navigation
  const getCurrentPeriod = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [periode, setPeriode] = useState(getCurrentPeriod());

  const formatPeriode = (p) => {
    const [year, month] = p.split('-');
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const navigatePeriod = (direction) => {
    const [year, month] = periode.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    if (newMonth < 1) { newMonth = 12; newYear--; }
    setPeriode(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, agentsRes] = await Promise.all([
        api.get(`/centre-paie/stats/${periode}`),
        api.get(`/centre-paie/agents/${periode}`)
      ]);
      setStats(statsRes.data);
      setAgents(agentsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les données', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [periode, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEdit = (agent) => {
    setEditingAgent(agent.agentId);
    setEditValues({
      primePanier: agent.primePanier || 0,
      primeExceptionnelle: agent.primeExceptionnelle || 0,
      primeChien: agent.primeChien || 0,
      indemniteKm: agent.indemniteKm || 0,
      indemniteNavigo: agent.indemniteNavigo || 0,
      acompte: agent.acompte || 0,
      absenceHeures: agent.absenceHeures || 0
    });
  };

  const cancelEdit = () => {
    setEditingAgent(null);
    setEditValues({});
  };

  const saveVariables = async (agentId) => {
    setSaving(true);
    try {
      await api.put(`/centre-paie/agents/${periode}/${agentId}/variables`, editValues);
      toast({ title: '✅ Enregistré', description: 'Variables mises à jour avec succès' });
      setEditingAgent(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const validateAgent = async (agentId) => {
    try {
      await api.post(`/centre-paie/agents/${periode}/${agentId}/validate`);
      toast({ title: '✅ Validé', description: 'Variables de paie validées' });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de valider', variant: 'destructive' });
    }
  };

  const exportData = async () => {
    try {
      const response = await api.post(`/centre-paie/export/${periode}`);
      toast({ title: '📤 Export réussi', description: response.data.message });
      fetchData();
    } catch (error) {
      toast({ 
        title: 'Export impossible', 
        description: error.response?.data?.detail || 'Validez d\'abord les fiches', 
        variant: 'destructive' 
      });
    }
  };

  const calculateTotal = (agent) => {
    const tauxHoraire = 12.608; // Taux moyen coef 140
    const base = agent.heuresTotal * tauxHoraire;
    const nuit = agent.heuresNuit * tauxHoraire * 0.1;
    const dimanche = agent.heuresDimanche * tauxHoraire * 0.1;
    const sup25 = agent.heuresSup25 * tauxHoraire * 0.25;
    const sup50 = agent.heuresSup50 * tauxHoraire * 0.5;
    const primes = (agent.primePanier || 0) + (agent.primeExceptionnelle || 0) + (agent.primeChien || 0);
    const indemnites = (agent.indemniteKm || 0) + (agent.indemniteNavigo || 0);
    return base + nuit + dimanche + sup25 + sup50 + primes + indemnites - (agent.acompte || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calculator className="w-7 h-7 text-blue-600" />
              Variables de Paie Mensuelles
            </h1>
            <p className="text-gray-500 mt-1">
              Saisie et validation des éléments variables de paie
            </p>
          </div>

          {/* Period Navigation */}
          <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-2 shadow-sm border">
            <Button variant="ghost" size="sm" onClick={() => navigatePeriod(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
              {formatPeriode(periode)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => navigatePeriod(1)}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalAgents || 0}</p>
                <p className="text-xs text-gray-500">Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalHeures || 0}h</p>
                <p className="text-xs text-gray-500">Heures totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Moon className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalHeuresNuit || 0}h</p>
                <p className="text-xs text-gray-500">Heures nuit</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Sun className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalHeuresDimanche || 0}h</p>
                <p className="text-xs text-gray-500">H. Dimanche</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.agentsValides || 0}</p>
                <p className="text-xs text-gray-500">Validés</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Euro className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-gray-900">{(stats?.masseSalarialeBrute || 0).toLocaleString()}€</p>
                <p className="text-xs text-gray-500">Masse brute</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Récapitulatif par agent
            </CardTitle>
            <Button 
              onClick={exportData}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!agents.some(a => a.statut === 'valide')}
            >
              <Download className="w-4 h-4 mr-2" />
              Exporter pour comptabilité
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 text-gray-600 font-medium">Agent</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Heures base</th>
                  <th className="text-center p-3 text-gray-600 font-medium">H. Nuit</th>
                  <th className="text-center p-3 text-gray-600 font-medium">H. Dim.</th>
                  <th className="text-center p-3 text-gray-600 font-medium">H. Sup</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Primes</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Indemnités</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Acompte</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Total brut</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Statut</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center p-8 text-gray-400">
                      <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-500 opacity-70" />
                      <p className="text-base font-medium text-gray-600 mb-2">Aucune garde planifiee pour cette periode</p>
                      <p className="text-sm text-gray-400 max-w-md mx-auto">
                        Les variables de paie sont calculees a partir des gardes du module Planning. Ajoutez des gardes pour vos agents pour voir les donnees.
                      </p>
                    </td>
                  </tr>
                ) : (
                  agents.map((agent) => (
                    <tr key={agent.agentId} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                            {agent.agentName?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{agent.agentName}</p>
                            <p className="text-xs text-gray-500">{agent.nbShifts} shifts</p>
                          </div>
                        </div>
                      </td>
                      <td className="text-center p-3 font-semibold text-gray-900">{agent.heuresTotal}h</td>
                      <td className="text-center p-3 text-purple-600">{agent.heuresNuit}h</td>
                      <td className="text-center p-3 text-orange-600">{agent.heuresDimanche}h</td>
                      <td className="text-center p-3 text-yellow-600">
                        {(agent.heuresSup25 + agent.heuresSup50).toFixed(1)}h
                      </td>

                      {editingAgent === agent.agentId ? (
                        <>
                          <td className="text-center p-2">
                            <Input
                              type="number"
                              value={editValues.primePanier + editValues.primeExceptionnelle + editValues.primeChien}
                              onChange={(e) => setEditValues({...editValues, primePanier: parseFloat(e.target.value) || 0})}
                              className="w-20 h-8 text-center text-sm"
                              placeholder="€"
                            />
                          </td>
                          <td className="text-center p-2">
                            <Input
                              type="number"
                              value={editValues.indemniteKm + editValues.indemniteNavigo}
                              onChange={(e) => setEditValues({...editValues, indemniteKm: parseFloat(e.target.value) || 0})}
                              className="w-20 h-8 text-center text-sm"
                              placeholder="€"
                            />
                          </td>
                          <td className="text-center p-2">
                            <Input
                              type="number"
                              value={editValues.acompte}
                              onChange={(e) => setEditValues({...editValues, acompte: parseFloat(e.target.value) || 0})}
                              className="w-20 h-8 text-center text-sm"
                              placeholder="€"
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="text-center p-3 text-green-600">
                            {((agent.primePanier || 0) + (agent.primeExceptionnelle || 0) + (agent.primeChien || 0)).toFixed(0)}€
                          </td>
                          <td className="text-center p-3 text-blue-600">
                            {((agent.indemniteKm || 0) + (agent.indemniteNavigo || 0)).toFixed(0)}€
                          </td>
                          <td className="text-center p-3 text-red-600">
                            {(agent.acompte || 0).toFixed(0)}€
                          </td>
                        </>
                      )}

                      <td className="text-center p-3 font-bold text-gray-900">
                        {calculateTotal(agent).toFixed(0)}€
                      </td>

                      <td className="text-center p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                          ${agent.statut === 'valide' ? 'bg-emerald-100 text-emerald-700' : 
                            agent.statut === 'exporte' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'}`}>
                          {agent.statut === 'valide' ? '✓ Validé' : 
                           agent.statut === 'exporte' ? '📤 Exporté' : '⏳ Brouillon'}
                        </span>
                      </td>

                      <td className="text-center p-3">
                        {editingAgent === agent.agentId ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm"
                              onClick={() => saveVariables(agent.agentId)}
                              disabled={saving}
                              className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2"
                            >
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={cancelEdit}
                              className="h-8 px-2"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => startEdit(agent)}
                              className="text-blue-600 hover:text-blue-800 h-8 px-2"
                            >
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            {agent.statut !== 'valide' && agent.statut !== 'exporte' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => validateAgent(agent.agentId)}
                                className="text-emerald-600 hover:text-emerald-800 h-8 px-2"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-6 bg-white rounded-lg p-4 border">
        <h3 className="font-semibold text-gray-900 mb-3">Légende des majorations (Convention 1351)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-gray-600">Heures de nuit (21h-6h): <strong>+10%</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span className="text-gray-600">Dimanche: <strong>+10%</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600">H. Sup 25% (8 premières)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600">H. Sup 50% (suivantes)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariablesPaie;
